'use client'

import { ExternalLink } from 'lucide-react'
import { sanitizeHref } from '@/lib/sanitize'

interface SubredditCardProps {
  subreddit: string
  mentionCount: number
  avgSentiment: string
  avgSeedingRisk: number
  topPost: { title: string; permalink: string } | null
}

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#22c55e',
  neutral: '#FDC304',
  negative: '#F90B8A',
  mixed: '#8b5cf6',
}

function getRiskColor(risk: number): string {
  if (risk >= 70) return '#F90B8A'
  if (risk >= 40) return '#FDC304'
  return '#22c55e'
}

export default function SubredditCard({
  subreddit,
  mentionCount,
  avgSentiment,
  avgSeedingRisk,
  topPost,
}: SubredditCardProps) {
  const sentimentColor = SENTIMENT_COLORS[avgSentiment] || '#FDC304'
  const riskColor = getRiskColor(avgSeedingRisk)

  return (
    <div className='bg-background border border-border/60 rounded-xl p-4 flex flex-col gap-3'>
      <div className='flex items-center justify-between'>
        <a
          href={`https://www.reddit.com/r/${subreddit}`}
          target='_blank'
          rel='noopener noreferrer'
          className='text-sm font-semibold text-foreground hover:text-primary transition-colors flex items-center gap-1'
        >
          r/{subreddit}
          <ExternalLink size={12} className='text-muted-foreground' />
        </a>
        <span
          className='text-xs font-bold rounded-full px-2.5 py-0.5'
          style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
        >
          {mentionCount} mention{mentionCount !== 1 ? 's' : ''}
        </span>
      </div>

      <div className='flex items-center gap-3'>
        <div className='flex items-center gap-1.5'>
          <span
            className='h-2 w-2 rounded-full'
            style={{ backgroundColor: sentimentColor }}
          />
          <span className='text-xs text-foreground/70 capitalize'>
            {avgSentiment}
          </span>
        </div>
        <div className='flex items-center gap-1.5'>
          <span
            className='h-2 w-2 rounded-full'
            style={{ backgroundColor: riskColor }}
          />
          <span className='text-xs text-foreground/70'>
            Avg risk: {avgSeedingRisk}%
          </span>
        </div>
      </div>

      {topPost &&
        (() => {
          const safePermalink = sanitizeHref(topPost.permalink, {
            allowedSchemes: ['https:'],
          })
          if (!safePermalink) return null
          return (
            <a
              href={safePermalink}
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs text-foreground/60 hover:text-foreground/80 transition-colors line-clamp-2 leading-relaxed'
            >
              Top: {topPost.title}
            </a>
          )
        })()}
    </div>
  )
}
